import React from 'react';
import { AnimatedSection } from '../ui/AnimatedSection';
import { useTenant } from '../../context/TenantContext';
import { focalPos } from '../course-builder/slides/FocalPointPicker';
import { MarketingSectionHeader } from './SectionHeader';

const DEFAULT_HEADING = 'Practitioners. *Not pundits.*';

// TODO: instructor.userId auto-resolution from users table — needs a public-read RLS policy first.
export const Instructor: React.FC = () => {
  const { tenant } = useTenant();
  const marketing = tenant?.branding.marketing;
  const instructor = marketing?.instructor;

  if (!instructor) return null;

  const name = instructor.name?.trim();
  const role = instructor.role?.trim();
  const imageUrl = instructor.imageUrl?.trim();
  const bio = instructor.bio?.trim();
  const pullQuote = instructor.pullQuote?.trim();

  // If nothing meaningful supplied, hide entirely.
  if (!name && !imageUrl && !bio && !pullQuote) return null;

  const heading = marketing?.copy?.instructorHeading?.trim() || DEFAULT_HEADING;
  const subhead = marketing?.copy?.instructorSubhead?.trim();

  return (
    <section id="instructor" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <MarketingSectionHeader
          marker="§ Faculty"
          rubric="Who teaches here"
          headline={heading}
          subhead={subhead}
        />

        <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-16 items-center">
          <AnimatedSection>
            <figure className="relative aspect-[4/5] bg-gray-100 rounded-2xl overflow-hidden">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={name ? `Portrait of ${name}` : ''}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: focalPos(instructor.imagePosition) }}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                  <span className="font-serif text-6xl text-primary-400">
                    {name?.[0] ?? '·'}
                  </span>
                </div>
              )}
              <span className="absolute top-4 left-4 bg-white text-gray-900 font-mono text-[10px] tracking-wider uppercase px-2.5 py-1 rounded">
                Faculty · Lead
              </span>
            </figure>
          </AnimatedSection>

          <AnimatedSection delay={0.15}>
            {(name || role) && (
              <div className="mb-7">
                {name && (
                  <div className="font-serif text-2xl md:text-3xl font-medium tracking-tight text-gray-900">
                    {name}
                  </div>
                )}
                {role && (
                  <div className="font-mono text-xs uppercase tracking-[0.14em] text-primary-600 mt-1.5">
                    {role}
                  </div>
                )}
              </div>
            )}

            {pullQuote && (
              <blockquote className="font-serif italic font-light text-2xl md:text-3xl leading-snug tracking-tight text-gray-900 border-l-2 border-primary-600 pl-6 mb-7 text-balance">
                {pullQuote.startsWith('"') ? pullQuote : `"${pullQuote}"`}
              </blockquote>
            )}

            {bio && (
              <div className="text-gray-600 text-base md:text-lg leading-relaxed max-w-[56ch] space-y-3">
                {bio.split(/\n\n+/).map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};
